/**
 * Route prefetching for the command palette.
 *
 * Warms the webpack chunk for a likely-next route so navigation is instant.
 * Chunk names here mirror the router (chunk-*) so a prefetch pulls
 * the same group chunk the route will use — no duplicate chunks. Prefetching
 * is best-effort: failures are swallowed and retried later.
 */
import { useEffect } from 'react'

/** Routes already warmed, so we don't re-import on every palette open. */
const prefetched = new Set<string>()

/**
 * Path → chunk loader. `webpackPrefetch` also emits a <link rel="prefetch">.
 * Chunk names must match the router's literals to hit the same chunk.
 */
const ROUTE_CHUNK_MAP: Record<string, () => Promise<unknown>> = {
  '/transactions': () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "chunk-asset" */ '../../containers/transactionsPage'
    ),
  '/schedule/events': () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "chunk-asset" */ '../../containers/eventsPage'
    ),
  '/schedule/venues': () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "chunk-asset" */ '../../containers/venuesPage'
    ),
  '/user-management': () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "chunk-onboarding" */ '../../containers/usersPage'
    ),
  '/organizations': () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "chunk-onboarding" */ '../../containers/organizations'
    ),
  '/communications': () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "chunk-onboarding" */ '../../containers/communications'
    ),
  '/ai-evaluations': () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "chunk-insights" */ '../../containers/aiEvaluationsPage'
    ),
  '/chat-notebook': () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "chunk-workspace" */ '../../components/chatNotebookPage'
    ),
}

/** Prefetch one route's chunk. No-op if unknown or already warmed. */
export const prefetchRoute = (path: string): void => {
  if (prefetched.has(path)) return
  const loader = ROUTE_CHUNK_MAP[path]
  if (!loader) return
  prefetched.add(path)
  loader().catch(() => {
    // Allow a later retry if the fetch failed.
    prefetched.delete(path)
  })
}

export const prefetchRoutes = (paths: ReadonlyArray<string>): void => {
  paths.forEach(prefetchRoute)
}

/** The routes we background-warm shortly after the app mounts. */
const HIGH_PRIORITY: ReadonlyArray<string> = [
  '/transactions',
  '/user-management',
  '/organizations',
  '/schedule/events',
]

/** Prefetch the most common destinations during idle time after mount. */
export const prefetchHighPriorityRoutes = (): void => {
  const run = () => prefetchRoutes(HIGH_PRIORITY)
  const ric = (
    window as unknown as {
      requestIdleCallback?: (cb: () => void) => void
    }
  ).requestIdleCallback
  if (typeof ric === 'function') {
    ric(run)
  } else {
    setTimeout(run, 3000)
  }
}

/**
 * Prefetch the routes currently visible in the palette when it opens. A real
 * hook: the effect only runs when `open` or `visiblePaths` change (not on every
 * render). Caps at 5 to avoid a burst of requests.
 */
export const usePrefetchOnOpen = (
  open: boolean,
  visiblePaths: ReadonlyArray<string>
): void => {
  useEffect(() => {
    if (open && visiblePaths.length > 0) {
      prefetchRoutes(visiblePaths.slice(0, 5))
    }
  }, [open, visiblePaths])
}
